<template>
  <div class="bg-white rounded-xl shadow-sm h-[600px] flex flex-col">
    <div class="p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
      <h2 class="text-xl font-bold text-gray-800">开放问诊</h2>
      <div class="flex items-center space-x-3">
        <button
          v-if="status !== 'streaming'"
          @click="restartDiagnosis"
          class="px-4 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
        >
          重新开始
        </button>
        <button
          v-else
          @click="cancelStreaming"
          class="px-4 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
        >
          取消生成
        </button>
      </div>
    </div>

    <div class="flex-1 p-6 max-w-5xl mx-auto w-full flex flex-col space-y-4 min-h-0">
      <div v-if="status === 'error'" class="p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 flex items-center justify-between">
        <div class="text-sm">
          {{ error_message || '请求失败' }}
        </div>
        <button
          @click="restartDiagnosis"
          class="px-4 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white transition"
        >
          重试
        </button>
      </div>

      <div ref="chat_container" class="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3 overscroll-contain">
        <div v-if="chat_messages.length === 0" class="text-sm text-gray-500 text-center py-4">
          {{ status === 'streaming' ? '正在生成首题...' : '初始化中...' }}
        </div>

        <div
          v-for="msg in chat_messages"
          :key="msg.message_id"
          class="flex"
          :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] rounded-2xl px-4 py-3 shadow-sm border"
            :class="msg.role === 'user' ? 'bg-emerald-600 text-white border-emerald-600/20' : 'bg-white text-gray-800 border-gray-100'"
          >
            <div v-if="msg.type === 'answer'" class="space-y-1">
              <div class="text-sm whitespace-pre-wrap">{{ msg.option_text }}</div>
            </div>

            <div v-else-if="msg.type === 'question'" class="space-y-3">
              <div class="text-sm whitespace-pre-wrap">{{ msg.question }}</div>
              <div
                v-if="status === 'question_ready' && msg.message_id === current_question_message_id"
                class="space-y-2"
              >
                <button
                  v-for="(option_text, option_index) in (msg.options || [])"
                  :key="option_index"
                  @click="chooseOption(option_index)"
                  class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition flex items-start space-x-2"
                >
                  <span class="text-sm text-gray-700">{{ option_text }}</span>
                </button>
              </div>

              <div
                v-else-if="status === 'awaiting_complaint' && msg.message_id === complaint_question_message_id"
                class="space-y-2"
              >
                <textarea
                  v-model="complaint_text"
                  rows="4"
                  class="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition text-sm text-gray-800 bg-white"
                  placeholder="例如：发热、咳嗽、头痛等，持续多久，伴随症状..."
                />
                <div class="flex items-center justify-end">
                  <button
                    @click="submitComplaint"
                    :disabled="status === 'streaming' || !complaint_text.trim()"
                    class="px-4 py-2 rounded-lg text-sm text-white transition"
                    :class="!complaint_text.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'"
                  >
                    提交
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="msg.type === 'result'" class="space-y-3">
              <div class="font-semibold text-sm">诊断结果</div>
              <div class="space-y-2 text-sm">
                <div>
                  <div class="text-xs text-gray-500">诊断</div>
                  <div class="mt-0.5 whitespace-pre-wrap">{{ msg.content?.diagnosis || '-' }}</div>
                </div>
                <div>
                  <div class="text-xs text-gray-500">证型</div>
                  <div class="mt-0.5 whitespace-pre-wrap">{{ msg.content?.syndrome || '-' }}</div>
                </div>
                <div>
                  <div class="text-xs text-gray-500">处方建议</div>
                  <div class="mt-0.5 whitespace-pre-wrap">{{ msg.content?.prescription || '-' }}</div>
                </div>
              </div>
            </div>

            <div v-else-if="msg.type === 'completed'" class="space-y-3">
              <div class="flex items-start space-x-2 bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="text-sm text-gray-800">{{ msg.content }}</div>
              </div>
              <button
                @click="copyDiagnosisRecord"
                class="w-full mt-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition flex items-center justify-center space-x-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                <span>复制问诊记录</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 问诊完成后的舌苔图片上传 -->
        <div v-if="status === 'completed'" class="flex justify-start mt-4">
          <div class="w-full max-w-[85%] rounded-2xl p-4 shadow-sm border bg-white border-gray-100 space-y-4">
            <div 
              class="flex justify-between items-center cursor-pointer"
              @click="is_tongue_upload_expanded = !is_tongue_upload_expanded"
            >
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span class="font-medium text-gray-700">上传舌苔图片 (可选)</span>
              </div>
              <svg 
                :class="{'rotate-180': is_tongue_upload_expanded}" 
                class="w-5 h-5 text-gray-500 transition-transform" 
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <div v-show="is_tongue_upload_expanded" class="pt-4 border-t border-gray-100">
              <p class="text-sm text-gray-500 mb-4">上传舌苔照片可帮助医生更准确辨证，建议自然光下拍摄、避免滤镜。</p>
              
              <div v-if="tongue_image_url" class="relative inline-block">
                <img :src="tongue_image_url" class="w-32 h-32 object-cover rounded-lg border border-gray-200" />
                <div class="absolute -top-2 -right-2 flex space-x-1">
                  <button @click="triggerTongueUpload" class="p-1.5 bg-white rounded-full shadow border hover:bg-gray-50 transition" title="重新拍摄/选取">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button @click="removeTongueImage" class="p-1.5 bg-white rounded-full shadow border hover:bg-red-50 transition" title="删除">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div v-else class="flex items-start space-x-4">
                <div 
                  class="w-32 h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:text-emerald-600 transition group"
                  @click="triggerTongueUpload"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400 group-hover:text-emerald-500 mb-2 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span class="text-xs text-gray-500 group-hover:text-emerald-600 transition">点击拍摄/选取</span>
                </div>
                <div class="flex-1">
                  <div class="text-xs text-gray-500 mb-2">示例图：</div>
                  <img :src="tongue_example_image" class="w-20 h-20 object-cover rounded border border-gray-200" />
                </div>
              </div>

              <!-- 上传进度与状态 -->
              <div v-if="tongue_uploading" class="mt-4">
                <div class="flex justify-between text-xs text-gray-500 mb-1">
                  <span>正在上传...</span>
                  <span>{{ tongue_upload_progress }}%</span>
                </div>
                <div class="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-full bg-emerald-500 transition-all duration-300" :style="{width: tongue_upload_progress + '%'}"></div>
                </div>
              </div>
              <div v-if="tongue_upload_error" class="mt-2 text-xs text-red-500 flex items-center space-x-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{{ tongue_upload_error }}</span>
              </div>
              
              <input 
                type="file" 
                ref="tongueFileInput" 
                class="hidden" 
                accept="image/jpeg,image/png" 
                @change="handleTongueFileChange" 
              />
            </div>
          </div>
        </div>

        <div v-if="status === 'streaming' && chat_messages.length > 0" class="flex justify-start">
          <div class="max-w-[85%] rounded-2xl px-4 py-3 shadow-sm border bg-white text-gray-800 border-gray-100">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-500">正在分析中</span>
              <div class="flex space-x-1">
                <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest } from '@/utils/request'
import tongue_example_image from '@/assets/tongue.jpeg'

export default {
  name: 'OpenDiagnosisPanel',
  props: {
    patientId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      status: 'idle',
      chat_messages: [],
      complaint_text: '',
      session_id: null,
      current_question: null,
      current_question_event_id: null,
      partial_question_message_id: null,
      complaint_question_message_id: null,
      current_question_message_id: null,
      selected_option_index: null,
      result_content: null,
      error_message: '',
      abort_controller: null,
      
      // 舌苔图片上传相关
      is_tongue_upload_expanded: false,
      tongue_image_url: '',
      tongue_image_path: '',
      tongue_example_image,
      tongue_uploading: false,
      tongue_upload_progress: 0,
      tongue_upload_error: ''
    }
  },
  mounted() {
    this.restartDiagnosis()
  },
  beforeUnmount() {
    this.cancelStreaming()
  },
  watch: {
    chat_messages: {
      handler() {
        this.scrollToBottom()
      },
      deep: true
    },
    status(new_status) {
      if (new_status === 'streaming') {
        this.scrollToBottom()
      }
    }
  },
  methods: {
    cancelStreaming() {
      if (this.abort_controller) {
        try {
          this.abort_controller.abort()
        } catch (e) {
          // ignore
        }
      }
      this.abort_controller = null
      if (this.status === 'streaming') {
        if (this.result_content) {
          this.status = 'result_ready'
          return
        }
        if (this.current_question) {
          this.status = 'question_ready'
          return
        }
        if (this.partial_question_message_id) {
          this.status = 'question_text_ready'
          return
        }
        this.status = 'idle'
      }
    },
    restartDiagnosis() {
      this.cancelStreaming()
      this.status = 'awaiting_complaint'
      this.chat_messages = []
      this.complaint_text = ''
      this.session_id = null
      this.current_question = null
      this.current_question_event_id = null
      this.partial_question_message_id = null
      this.complaint_question_message_id = null
      this.current_question_message_id = null
      this.selected_option_index = null
      this.result_content = null
      this.error_message = ''
      
      this.is_tongue_upload_expanded = false
      this.tongue_image_url = ''
      this.tongue_image_path = ''
      this.tongue_uploading = false
      this.tongue_upload_progress = 0
      this.tongue_upload_error = ''
      
      const message_id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
      this.complaint_question_message_id = message_id
      this.chat_messages.push({
        message_id,
        role: 'assistant',
        type: 'question',
        question: '请描述您的主要症状',
        options: []
      })
      this.scrollToBottom()
    },
    chooseOption(option_index) {
      if (this.status !== 'question_ready') return
      if (!this.current_question) return
      const option_text = (this.current_question.options || [])[option_index]
      if (!option_text) return

      const option_id = String(option_index + 1)
      this.selected_option_index = option_index
      this.chat_messages.push({
        message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        role: 'user',
        type: 'answer',
        option_id,
        option_text
      })
      this.scrollToBottom()
      this.submitAnswer()
    },
    async submitComplaint() {
      if (this.status !== 'awaiting_complaint') return
      const complaint_text = this.complaint_text.trim()
      if (!complaint_text) return

      this.chat_messages.push({
        message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        role: 'user',
        type: 'answer',
        option_id: '主诉',
        option_text: complaint_text
      })
      this.scrollToBottom()
      await this.startDiagnosis(complaint_text)
    },
    async copyDiagnosisRecord() {
      if (!this.chat_messages || this.chat_messages.length === 0) {
        alert('暂无问诊记录可复制');
        return;
      }

      let content = `【中医开放问诊记录】\n`;
      content += `时间：${new Date().toLocaleString()}\n`;
      content += `------------------------\n`;
      
      this.chat_messages.forEach((msg) => {
        if (msg.type === 'question' && msg.question) {
          content += `\n【机构】${msg.question}\n`;
        } else if (msg.type === 'answer' && msg.option_text) {
          content += `【用户】${msg.option_text}\n`;
        } else if (msg.type === 'result' && msg.content) {
          content += `\n------------------------\n`;
          content += `【诊断结果】\n`;
          content += `诊断：${msg.content.diagnosis || '-'}\n`;
          content += `证型：${msg.content.syndrome || '-'}\n`;
          content += `处方建议：${msg.content.prescription || '-'}\n`;
        }
      });
      
      content += `\n------------------------\n`;

      // 尝试使用 navigator.clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(content);
          alert('问诊记录已复制到剪贴板');
          return;
        } catch (err) {
          console.warn('Clipboard API failed, trying fallback...', err);
        }
      }

      // 降级方案：使用 textarea + execCommand
      try {
        const textArea = document.createElement("textarea");
        textArea.value = content;
        
        // 确保元素不可见但存在于 DOM 中
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          alert('问诊记录已复制到剪贴板');
        } else {
          throw new Error('execCommand returned false');
        }
      } catch (err) {
        console.error('Copy failed:', err);
        alert('复制失败，请手动截图保存');
      }
    },
    async startDiagnosis(complaint_text) {
      this.status = 'streaming'
      this.error_message = ''
      this.abort_controller = new AbortController()

      try {
        // 虽然文档没写 complaint，但为了保持一致性并尝试传递，我们将其加入 body
        // 如果后端忽略它，也不影响
        const response = await httpRequest('post', '/v1/patient/diagnosis/open/start', {
          headers: {
            'Client-Type': '1',
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          query: {
            stream: 1
          },
          body: {
            patient_id: Number(this.patientId),
            complaint: complaint_text // 尝试传递主诉
          },
          stream: true,
          timeout: 0,
          signal: this.abort_controller.signal
        })
        await this.consumeSseResponse(response, {
          on_delta: () => {},
          on_question_text: (payload) => {
            this.applyQuestionText(payload)
          },
          on_done: (payload) => {
            this.session_id = payload.session_id
            if (payload.type === 'question') {
              this.applyFinalQuestion(payload)
              this.selected_option_index = null
              this.result_content = null
              this.status = 'question_ready'
              this.scrollToBottom()
            } else if (payload.type === 'result') {
              this.current_question = null
              this.partial_question_message_id = null
              this.selected_option_index = null
              this.result_content = payload.content || null
              this.status = 'result_ready'
              this.current_question_message_id = null
              this.chat_messages.push({
                message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant',
                type: 'result',
                content: this.result_content
              })
              this.scrollToBottom()
            } else if (payload.type === 'completed') {
              this.status = 'completed'
              this.chat_messages.push({
                message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant',
                type: 'completed',
                content: '问诊已完成，请将问诊过程内容，提供给您的机构，以获取更准确的建议。'
              })
              this.scrollToBottom()
            }
          },
          on_error: (message) => {
            this.error_message = message || '请确认内容与中医辨证有关！'
            this.status = 'error'
          }
        })
      } catch (e) {
        if (e && e.name === 'AbortError') return
        this.error_message = e && e.message ? e.message : '网络异常'
        this.status = 'error'
      } finally {
        this.abort_controller = null
      }
    },
    async submitAnswer() {
      if (!this.session_id || !this.current_question) return
      if (this.selected_option_index === null) return

      const option_text = (this.current_question.options || [])[this.selected_option_index]
      if (!option_text) return
      const option_id = String(this.selected_option_index + 1)

      this.cancelStreaming()
      this.status = 'streaming'
      this.error_message = ''
      this.abort_controller = new AbortController()

      try {
        const body = {
          option_id,
          option_text
        }
        if (this.current_question_event_id !== null && this.current_question_event_id !== undefined) {
          body.event_id = this.current_question_event_id
        }

        const response = await httpRequest('post', `/v1/patient/diagnosis/open/session/${this.session_id}/answer`, {
          headers: {
            'Client-Type': '1',
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          query: {
            stream: 1
          },
          body,
          stream: true,
          timeout: 0,
          signal: this.abort_controller.signal
        })

        await this.consumeSseResponse(response, {
          on_delta: () => {},
          on_question_text: (payload) => {
            this.applyQuestionText(payload)
          },
          on_done: (payload) => {
            if (payload.type === 'question') {
              this.applyFinalQuestion(payload)
              this.selected_option_index = null
              this.result_content = null
              this.status = 'question_ready'
              this.scrollToBottom()
            } else if (payload.type === 'result') {
              this.current_question = null
              this.partial_question_message_id = null
              this.selected_option_index = null
              this.result_content = payload.content || null
              this.status = 'result_ready'
              this.current_question_message_id = null
              this.chat_messages.push({
                message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant',
                type: 'result',
                content: this.result_content
              })
              this.scrollToBottom()
            } else if (payload.type === 'completed') {
              this.status = 'completed'
              this.chat_messages.push({
                message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant',
                type: 'completed',
                content: '问诊已完成，请将问诊过程内容，提供给您的机构，以获取更准确的建议。'
              })
              this.scrollToBottom()
            }
          },
          on_error: (message) => {
            this.error_message = message || '服务端错误'
            this.status = 'error'
          }
        })
      } catch (e) {
        if (e && e.name === 'AbortError') return
        this.error_message = e && e.message ? e.message : '网络异常'
        this.status = 'error'
      } finally {
        this.abort_controller = null
      }
    },
    applyQuestionText(payload) {
      if (!payload) return
      if (payload.session_id) {
        this.session_id = payload.session_id
      }
      const question_text = payload.content && typeof payload.content.question === 'string' ? payload.content.question : ''
      if (!question_text) return

      if (this.partial_question_message_id) {
        const message = this.chat_messages.find((m) => m && m.message_id === this.partial_question_message_id)
        if (message) {
          message.question = question_text
        }
      } else {
        const message_id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
        this.partial_question_message_id = message_id
        this.current_question_message_id = message_id
        this.chat_messages.push({
          message_id,
          role: 'assistant',
          type: 'question',
          question: question_text,
          options: []
        })
      }

      if (this.status === 'streaming') {
        this.status = 'question_text_ready'
      }
      this.scrollToBottom()
    },
    applyFinalQuestion(payload) {
      this.current_question = payload && payload.content ? payload.content : null
      const raw_event_id = this.current_question ? this.current_question.__event_id : null
      const parsed_event_id = raw_event_id === null || raw_event_id === undefined ? NaN : Number(raw_event_id)
      this.current_question_event_id = Number.isFinite(parsed_event_id) ? parsed_event_id : null
      const question_text = this.current_question && typeof this.current_question.question === 'string' ? this.current_question.question : ''
      const options = this.current_question && Array.isArray(this.current_question.options) ? this.current_question.options : []

      if (this.partial_question_message_id) {
        const message = this.chat_messages.find((m) => m && m.message_id === this.partial_question_message_id)
        if (message) {
          message.question = question_text
          message.options = options
          this.current_question_message_id = message.message_id
          this.partial_question_message_id = null
          return
        }
      }

      const message_id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
      this.current_question_message_id = message_id
      this.partial_question_message_id = null
      this.chat_messages.push({
        message_id,
        role: 'assistant',
        type: 'question',
        question: question_text,
        options
      })
    },
    async consumeSseResponse(response, handlers) {
      if (!response || !response.ok) {
        const status = response && response.status ? response.status : 0
        throw new Error(status ? `HTTP 错误：${status}` : 'HTTP 错误')
      }
      if (!response.body) {
        throw new Error('响应流为空')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let pending_text = ''
      let stream_done = false
      let terminal_payload_emitted = false

      while (!stream_done) {
        const { done, value } = await reader.read()
        stream_done = done
        if (!value) continue

        pending_text += decoder.decode(value, { stream: true })
        pending_text = pending_text.replace(/\r\n/g, '\n')

        let event_end_index = pending_text.indexOf('\n\n')
        while (event_end_index !== -1) {
          const raw_event = pending_text.slice(0, event_end_index)
          pending_text = pending_text.slice(event_end_index + 2)

          const parsed_event = this.parseSseEvent(raw_event)
          if (parsed_event && parsed_event.type === 'delta') {
            const delta_text = parsed_event.content && typeof parsed_event.content.delta === 'string' ? parsed_event.content.delta : ''
            if (delta_text && handlers && typeof handlers.on_delta === 'function') {
              handlers.on_delta(delta_text)
            }
          } else if (parsed_event && parsed_event.type === 'question_text') {
            if (handlers && typeof handlers.on_question_text === 'function') {
              handlers.on_question_text(parsed_event)
            }
          } else if (parsed_event && parsed_event.type === 'done') {
            stream_done = true
            break
          } else if (parsed_event && parsed_event.type === 'question') {
            if (!terminal_payload_emitted && handlers && typeof handlers.on_done === 'function') {
              handlers.on_done(parsed_event)
            }
            terminal_payload_emitted = true
          } else if (parsed_event && parsed_event.type === 'result') {
            if (!terminal_payload_emitted && handlers && typeof handlers.on_done === 'function') {
              handlers.on_done(parsed_event)
            }
            terminal_payload_emitted = true
          } else if (parsed_event && parsed_event.type === 'completed') {
            if (!terminal_payload_emitted && handlers && typeof handlers.on_done === 'function') {
              handlers.on_done(parsed_event)
            }
            terminal_payload_emitted = true
          } else if (parsed_event && parsed_event.type === 'error') {
            if (handlers && typeof handlers.on_error === 'function') {
              handlers.on_error(parsed_event.message || '服务端错误')
            }
            stream_done = true
            break
          }

          event_end_index = pending_text.indexOf('\n\n')
        }
      }

      pending_text += decoder.decode()
    },
    parseSseEvent(raw_event) {
      const lines = String(raw_event || '').split('\n')
      const data_lines = []
      for (const line of lines) {
        if (line.startsWith('data:')) {
          data_lines.push(line.replace(/^data:\s?/, ''))
        }
      }

      const data_text = data_lines.join('\n').trim()
      if (!data_text) return null
      if (data_text === '[DONE]') return { type: 'done' }

      try {
        const payload = JSON.parse(data_text)
        return payload
      } catch (e) {
        return { type: 'delta', content: { delta: data_text } }
      }
    },
    scrollToBottom() {
      if (this._scroll_to_bottom_queued) return
      this._scroll_to_bottom_queued = true
      this.$nextTick(() => {
        this._scroll_to_bottom_queued = false
        const el = this.$refs.chat_container
        if (!el) return
        try {
          el.scrollTop = el.scrollHeight
        } catch (e) {
          // ignore
        }
      })
    },
    
    // 舌苔图片上传相关方法
    triggerTongueUpload() {
      this.$refs.tongueFileInput?.click()
    },
    
    removeTongueImage() {
      if (this.tongue_image_url && this.tongue_image_url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.tongue_image_url)
        } catch (e) {
          // ignore
        }
      }
      this.tongue_image_url = ''
      this.tongue_image_path = ''
      if (this.$refs.tongueFileInput) {
        this.$refs.tongueFileInput.value = ''
      }
    },
    
    async handleTongueFileChange(event) {
      const file = event.target.files[0]
      if (!file) return

      this.tongue_upload_error = ''
      this.tongue_upload_progress = 0
      
      // 校验大小 <= 2MB
      if (file.size > 2 * 1024 * 1024) {
        alert('图片大小不能超过2MB')
        event.target.value = ''
        return
      }
      
      // 校验格式
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        alert('仅支持JPG/PNG格式图片')
        event.target.value = ''
        return
      }
      
      if (this.tongue_image_url && this.tongue_image_url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.tongue_image_url)
        } catch (e) {
          // ignore
        }
      }
      this.tongue_image_url = URL.createObjectURL(file)
      
      // 直接上传，不再校验尺寸
      await this.uploadTongueImage(file)
    },
    
    async uploadTongueImage(file, retryCount = 3) {
      this.tongue_uploading = true
      this.tongue_upload_error = ''
      this.tongue_upload_progress = 10 // 初始进度
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('scene', 2) // 2: 问诊舌苔
        
        // 模拟进度
        const progressInterval = setInterval(() => {
          if (this.tongue_upload_progress < 90) {
            this.tongue_upload_progress += 10
          }
        }, 200)

        // 我们这里不使用封装好的 httpRequest，因为它没有原生进度回调且对 FormData 的支持可能不同，直接用 fetch
        const token = localStorage.getItem('token') || ''
        const response = await fetch('/api/v1/common/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Client-Type': '1'
            // FormData 自动设置 Content-Type
          },
          body: formData
        })
        
        clearInterval(progressInterval)
        this.tongue_upload_progress = 100
        
        const resData = await response.json()
        if (resData.code === 2001 && resData.data && resData.data.path) {
          const path = resData.data.path
          this.tongue_image_path = path.startsWith('http') ? path : `/${path}`
          
          // 上传成功后调用更新会话接口
          await this.updateSessionWithTongue()
        } else {
          throw new Error(resData.message || '上传失败')
        }
        
      } catch (error) {
        if (retryCount > 1) {
          this.tongue_upload_error = `上传失败，正在重试...(${4 - retryCount}/3)`
          await new Promise(resolve => setTimeout(resolve, 1000))
          return this.uploadTongueImage(file, retryCount - 1)
        } else {
          this.tongue_upload_error = error.message || '上传失败，错误码：500'
          this.tongue_image_path = ''
          if (this.$refs.tongueFileInput) {
            this.$refs.tongueFileInput.value = ''
          }
        }
      } finally {
        this.tongue_uploading = false
      }
    },
    
    async updateSessionWithTongue() {
      if (!this.session_id || !this.tongue_image_path) return
      
      try {
        await httpRequest('put', `/v1/patient/diagnosis/open/session/${this.session_id}`, {
          headers: {
            'Client-Type': '1'
          },
          body: {
            tongue_image: this.tongue_image_path
          }
        })
        alert('图片已上传')
      } catch (error) {
        console.error('更新会话失败:', error)
        alert('舌苔图片已上传，但更新问诊会话失败，请重试')
      }
    }
  }
}
</script>
